import jwtDecode from "jwt-decode";

export enum SCOPE {
  UNITS_WRITE = "units:write",
  UNITS_READ = "units:read",
  RESERVAITIONS_READ = "reservations:read",
  RESERVAITIONS_WRITE = "reservations:write",
  UNIT_TEST = "unit test",
  SMARTHOME_WRITE = "smart-home:write",
  SMARTHOME_REPORTS = "smart-home:reports",
}

class AuthService {
  // Token refresh promise resolver (for waiting on token refresh in embedded mode)
  private static tokenRefreshResolver: ((token: string) => void) | null = null;
  private static tokenRefreshPromise: Promise<string> | null = null;

  // Request token refresh from parent system (embedded mode)
  static requestTokenRefresh(): Promise<string> {
    if (window.parent !== window) {
      // Create a new promise for this refresh request
      AuthService.tokenRefreshPromise = new Promise((resolve) => {
        AuthService.tokenRefreshResolver = resolve;
      });

      // Request new token from parent
      window.parent.postMessage({ type: "REQUEST_TOKEN_REFRESH" }, "*");

      // Return promise that resolves when token is updated
      return AuthService.tokenRefreshPromise;
    }
    return Promise.resolve(AuthService.accessToken || "");
  }

  // Called when token is refreshed (from EmbeddedAuth component)
  static onTokenRefreshed(
    token: string,
    accessToken?: string,
    idToken?: string,
  ) {
    if (AuthService.tokenRefreshResolver) {
      // Update tokens in localStorage (same format as OneLogin)
      if (accessToken && idToken) {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("idToken", idToken);
      } else if (token) {
        localStorage.setItem("accessToken", token);
        localStorage.setItem("idToken", token);
      }
      AuthService.tokenRefreshResolver(token || accessToken || "");
      AuthService.tokenRefreshResolver = null;
      AuthService.tokenRefreshPromise = null;
    }
  }

  // Check if we're using external auth (for backward compatibility)
  // Now tokens are stored the same way regardless of source
  static isUsingExternalAuth(): boolean {
    // This is now mainly for detecting if we're in an iframe context
    // Tokens are stored the same way (accessToken/idToken) regardless
    return window.parent !== window;
  }

  // Tokens are now stored in the same format as OneLogin
  // EmbeddedAuth stores them in accessToken/idToken, same as ImplicitFlow
  static get accessToken() {
    return localStorage.getItem("accessToken");
  }

  static get idToken() {
    return localStorage.getItem("idToken");
  }
  static get userEmail() {
    // Extract email from idToken (works for both OneLogin and embedded tokens)
    const idToken = AuthService.idToken;
    if (idToken) {
      try {
        const decoded = jwtDecode<any>(idToken);
        return decoded.email || decoded.sub || undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
  static getScopes(): SCOPE[] | null {
    // Extract scopes from accessToken (works for both OneLogin and embedded tokens)
    const token = AuthService.accessToken;
    if (!token) return null;
    try {
      const decoded = jwtDecode<any>(token);
      return decoded.scopes as SCOPE[];
    } catch {
      return null;
    }
  }

  //just only has to have one of the requiredScope
  static hasScope(requiredScope: SCOPE | SCOPE[]) {
    const availableScopes = AuthService.getScopes();
    if (!availableScopes) return false;
    const scopes = Array.isArray(requiredScope)
      ? requiredScope
      : [requiredScope];
    return scopes.some((s) => availableScopes.includes(s));
  }
}

export default AuthService;
